// src/Views/HowIThinkPage.js — Intellectual foundations + thinking tools
import React from 'react';
import { Helmet } from 'react-helmet';

const thinkingTools = [
  {
    name: 'Made of Stars',
    description: 'Strategic intelligence through inferentialist token logic. Built on Brandom, superforecasting research, and early warning methodology.',
  },
  {
    name: 'IdeatorGPT',
    description: 'An ideation coach that uses inferentialist reasoning to develop ideas through commitment and consequence, not brainstorming.',
  },
  {
    name: 'GameSignals',
    description: 'Games industry OSINT. Change drivers, forecast ledgers, signal schemas, and source credibility scoring.',
  },
  {
    name: 'Security By Dialogue',
    description: 'Interactive security consultant for LLM-based systems. Threat modeling through conversation.',
    link: 'https://chatgpt.com/g/g-67bcdc8964688191882b74211e19fd82-security-by-dialogue',
  },
  {
    name: 'Researcher/Playtest GPT',
    description: 'Structured research prep that prevents common documentation errors and generates submission-ready plans.',
  },
  {
    name: 'GPT Development Assistant',
    description: 'A development companion with a curated knowledgebase so builders can focus on building, not searching.',
    link: 'https://chatgpt.com/g/g-67dad7f9c83c819198c6851afdb009cc-custom-gpt-developer',
  },
];

const styles = {
  page: {
    backgroundColor: 'var(--color-bg-canvas)',
    color: 'var(--color-text-on-dark)',
    minHeight: '100vh',
  },
  header: {
    padding: 'calc(var(--space-16) + 60px) var(--space-6) var(--space-8)',
    maxWidth: '800px',
    margin: '0 auto',
  },
  title: {
    fontSize: 'var(--font-size-4xl)',
    fontWeight: 'var(--font-weight-bold)',
    color: 'var(--color-text-on-dark)',
    marginBottom: 'var(--space-4)',
    lineHeight: 'var(--line-height-tight)',
  },
  intro: {
    fontSize: 'var(--font-size-md)',
    color: 'var(--color-text-on-dark-secondary)',
    lineHeight: 'var(--line-height-relaxed)',
  },
  section: {
    padding: '0 var(--space-6) var(--space-12)',
    maxWidth: '800px',
    margin: '0 auto',
  },
  sectionTitle: {
    fontSize: 'var(--font-size-xl)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-on-dark)',
    marginBottom: 'var(--space-4)',
    lineHeight: 'var(--line-height-tight)',
  },
  paragraph: {
    fontSize: 'var(--font-size-base)',
    color: 'var(--color-text-on-dark-body)',
    lineHeight: 'var(--line-height-relaxed)',
    marginBottom: 'var(--space-4)',
  },
  toolsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: 'var(--space-4)',
    marginTop: 'var(--space-6)',
  },
  toolCard: {
    backgroundColor: 'var(--color-bg-surface-2)',
    borderRadius: 'var(--radius-md)',
    padding: 'var(--space-5)',
    border: '1px solid var(--color-border-subtle)',
  },
  toolName: {
    fontSize: 'var(--font-size-base)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-on-dark)',
    marginBottom: 'var(--space-2)',
  },
  toolDesc: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-on-dark-secondary)',
    lineHeight: 'var(--line-height-relaxed)',
    margin: 0,
  },
  toolLink: {
    display: 'inline-block',
    marginTop: 'var(--space-2)',
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-accent-link)',
    textDecoration: 'none',
  },
  whisper: {
    padding: 'var(--space-8) var(--space-6) var(--space-16)',
    maxWidth: '800px',
    margin: '0 auto',
    borderTop: '1px solid var(--color-border-subtle)',
  },
  whisperText: {
    fontSize: 'var(--font-size-md)',
    color: 'var(--color-text-on-dark-secondary)',
    lineHeight: 'var(--line-height-relaxed)',
    fontStyle: 'italic',
  },
};

const HowIThinkPage = () => {
  return (
    <div style={styles.page}>
      <Helmet>
        <title>How I Think — Solomon Foshko | Design by Dialogue</title>
        <meta name="description" content="Competitive intelligence, systems thinking, inferentialism, and game design — the foundations behind every build. Plus externalized thinking tools anyone can use." />
      </Helmet>
      <div style={styles.header}>
        <h1 style={styles.title}>How I Think</h1>
        <p style={styles.intro}>
          I didn't come to AI from engineering. I came from competitive intelligence,
          game design, and a philosophical tradition called inferentialism. That combination
          shapes how I see every problem — as a system of signals, decisions, and commitments
          that can be made explicit and then built.
        </p>
      </div>

      {/* Competitive Intelligence */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Competitive Intelligence & Early Warning</h2>
        <p style={styles.paragraph}>
          Before I built anything with AI, I spent years in competitive intelligence — the
          discipline of detecting change early enough to act on it. That practice trained me
          to think in signals, hypotheses, and decision triggers rather than tasks and features.
        </p>
      </div>

      {/* Systems Thinking */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Systems Thinking</h2>
        <p style={styles.paragraph}>
          Every build starts with the same question: what is the system here? Not what features
          should it have — what are the moving parts, what are the feedback loops, and where
          does the system break? Gears of Destiny is literally a mechanical system. Montgomery
          Safety Lens is an information system. Same lens, different material.
        </p>
      </div>

      {/* Inferentialism */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Inferentialism</h2>
        <p style={styles.paragraph}>
          Robert Brandom's philosophy of language — the idea that meaning lives in the
          commitments and inferences between claims, not in labels — shaped how I design AI
          agents that reason rather than just respond. Made of Stars, my strategic intelligence
          GPT, is built on inferentialist token logic. It's not a gimmick. It's how the agent
          maintains coherent reasoning across long analytical chains.
        </p>
      </div>

      {/* Game Design */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Game Design as a Thinking Discipline</h2>
        <p style={styles.paragraph}>
          Game design is systems thinking with immediate feedback. When you design a board game,
          every rule is a hypothesis about player behavior, and every playtest is an experiment.
          That discipline — specify the system, watch it run, trace failures back to design
          assumptions — is exactly how I build software with AI agents.
        </p>
      </div>

      {/* Thinking Tools */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Thinking Tools</h2>
        <p style={styles.paragraph}>
          These are externalized versions of how I think. Each one encodes a specific reasoning
          discipline into a tool anyone can use. They're the generous layer — I share how I
          think; working with me is how you access how I build.
        </p>
        <div style={styles.toolsGrid}>
          {thinkingTools.map((tool) => (
            <div key={tool.name} style={styles.toolCard}>
              <h3 style={styles.toolName}>{tool.name}</h3>
              <p style={styles.toolDesc}>{tool.description}</p>
              {tool.link && (
                <a
                  href={tool.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={styles.toolLink}
                >
                  Try it →
                </a>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Whisper line */}
      <div style={styles.whisper}>
        <p style={styles.whisperText}>
          Every build follows the same discipline — a clarity framework that makes AI agents
          execute without ambiguity. The builds are public. The method is what you get when
          we work together.
        </p>
      </div>
    </div>
  );
};

export default HowIThinkPage;
